// 공통 코드 관리 화면 (tb_code_master)
// 전체 너비 인라인 편집 그리드 방식
import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAllCodes, fetchCodesByType, fetchCodeTypes,
  createCode, updateCode, deleteCode,
} from '../../api/codes';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import './CodeMasterPage.css';

const CodeMasterPage = () => {
  const { user, isAdmin } = useAuth();
  const userId = user?.user_id;

  const [codes,      setCodes]      = useState([]);
  const [codeTypes,  setCodeTypes]  = useState([]);
  const [filterType, setFilterType] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [changes,    setChanges]    = useState({});     // { id: { field: newVal } }
  const [newRows,    setNewRows]    = useState([]);     // [{ _tempId, code_type, user_id, ... }]
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [message,    setMessage]    = useState({ text: '', type: '' });

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const data = filterType ? await fetchCodesByType(filterType) : await fetchAllCodes();
      setCodes(data);
    } catch {
      showMessage('목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchCodeTypes().then(setCodeTypes).catch(() => {});
  }, []);

  useEffect(() => {
    loadCodes();
    setChanges({});
    setNewRows([]);
  }, [loadCodes]);

  // admin 전용 사용자 필터 목록
  const uniqueUsers = isAdmin
    ? [...new Set(codes.map(c => c.user_id))].sort()
    : [];

  // 클라이언트 사이드 사용자 필터 적용
  const visibleCodes = codes.filter(c =>
    !isAdmin || userFilter === 'all' || c.user_id === userFilter
  );

  const getTypeLabel = (ct) => codeTypes.find(t => t.code_type === ct)?.label || ct;

  // 변경값 조회 (없으면 원본)
  const getVal = (code, field) =>
    changes[code.id]?.[field] !== undefined ? changes[code.id][field] : code[field];

  const isRowChanged = (id) =>
    !!changes[id] && Object.keys(changes[id]).length > 0;

  const hasDirty =
    Object.keys(changes).some(id => Object.keys(changes[id]).length > 0) ||
    newRows.length > 0;

  const handleCellChange = (id, field, value) => {
    setChanges(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const handleNewRowChange = (tempId, field, value) => {
    setNewRows(prev => prev.map(r => r._tempId === tempId ? { ...r, [field]: value } : r));
  };

  const handleAddNewRow = (targetUserId) => {
    const defaultType = filterType || codeTypes[0]?.code_type || 'SECTOR';
    setNewRows(prev => [...prev, {
      _tempId:    `new_${Date.now()}`,
      code_type:  defaultType,
      user_id:    targetUserId,
      code_id:    '',
      code_name:  '',
      sort_order: 0,
      is_active:  true,
    }]);
  };

  const handleRemoveNewRow = (tempId) => {
    setNewRows(prev => prev.filter(r => r._tempId !== tempId));
  };

  const handleDelete = async (id, codeId) => {
    if (!window.confirm(`코드 ID "${codeId}"을(를) 삭제하시겠습니까?`)) return;
    setSaving(true);
    try {
      await deleteCode(id);
      showMessage('코드가 삭제되었습니다.');
      setChanges(prev => { const n = { ...prev }; delete n[id]; return n; });
      await loadCodes();
    } catch (err) {
      showMessage(err.response?.data?.detail || '삭제에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    for (const r of newRows) {
      if (!r.code_id.trim())   { showMessage('새 행의 코드 ID를 입력하세요.', 'error'); return; }
      if (!r.code_name.trim()) { showMessage('새 행의 코드명을 입력하세요.', 'error'); return; }
    }
    if (!hasDirty) { showMessage('변경된 내용이 없습니다.', 'error'); return; }

    setSaving(true);
    try {
      // 기존 행 수정분 PUT (admin 행은 can_edit=false라 changes에 없음)
      for (const id of Object.keys(changes)) {
        if (Object.keys(changes[id]).length > 0) {
          await updateCode(Number(id), changes[id]);
        }
      }
      // 신규 행 POST (user_id를 현재 사용자로 지정)
      for (const r of newRows) {
        const { _tempId, ...payload } = r;
        await createCode(payload);
      }
      showMessage('저장되었습니다.');
      setChanges({});
      setNewRows([]);
      await loadCodes();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * admin 코드 가져오기 (없는 항목만 newRows에 추가, DB 저장 안 함)
   * - admin 코드를 서버에서 조회 후 현재 그리드에 없는 code_id만 신규 행으로 추가
   */
  const handleImportFromAdmin = async (codeType) => {
    const typeLabel = codeType === 'SECTOR' ? '섹터' : '투자기간';
    const confirmMsg =
      `admin의 ${typeLabel} 코드 중 내 목록에 없는 항목만 추가합니다.\n` +
      `기존 내 ${typeLabel} 데이터는 그대로 유지됩니다.\n` +
      `저장 버튼을 눌러야 최종 반영됩니다. 계속하시겠습니까?`;
    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    try {
      // 서버에서 해당 타입 코드 조회 (응답: admin 코드 + 내 코드)
      const allCodes = await fetchCodesByType(codeType);
      const adminCodes = allCodes.filter(c => c.user_id === 'admin');

      // 현재 내가 보유한 code_id 목록 (로드된 행 + 미저장 신규 행)
      const existingCodeIds = new Set([
        ...codes
          .filter(c => c.code_type === codeType && c.user_id === userId)
          .map(c => c.code_id),
        ...newRows
          .filter(r => r.code_type === codeType)
          .map(r => r.code_id),
      ]);

      // 내게 없는 admin 코드만 필터
      const toAdd = adminCodes.filter(c => !existingCodeIds.has(c.code_id));

      if (toAdd.length === 0) {
        showMessage(`추가할 새 ${typeLabel} 항목이 없습니다. (이미 모두 보유 중)`, 'error');
        return;
      }

      const ts = Date.now();
      const newEntries = toAdd.map((c, i) => ({
        _tempId:    `new_${ts}_${i}`,
        code_type:  c.code_type,
        user_id:    userId,       // admin → 현재 사용자로 교체
        code_id:    c.code_id,
        code_name:  c.code_name,
        sort_order: c.sort_order,
        is_active:  c.is_active,
      }));

      setNewRows(prev => [...prev, ...newEntries]);
      showMessage(`${typeLabel} 코드 ${toAdd.length}개가 추가되었습니다. 저장 버튼을 눌러 반영하세요.`);
    } catch {
      showMessage('가져오기에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setUserFilter('all');
  };

  // 비고 포함: non-admin=7, admin=8
  const colSpan = isAdmin ? 8 : 7;

  const toolbar = (
    <>
      {/* 조회 조건 */}
      <select value={filterType} onChange={handleFilterTypeChange} className="filter-select">
        <option value="">전체</option>
        {codeTypes.map(t => (
          <option key={t.code_type} value={t.code_type}>{t.label} ({t.code_type})</option>
        ))}
      </select>

      {isAdmin && (
        <select value={userFilter} onChange={e => setUserFilter(e.target.value)} className="filter-select">
          <option value="all">전체 사용자</option>
          {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      )}

      {/* 공통 코드 추가: admin만 활성 */}
      <button
        className="btn btn-secondary"
        onClick={() => handleAddNewRow('admin')}
        disabled={saving || !isAdmin}
        title={!isAdmin ? '관리자만 사용 가능합니다' : undefined}
      >
        + 공통 코드 추가
      </button>

      {/* 내 코드 추가: 항상 활성 */}
      <button
        className="btn btn-secondary"
        onClick={() => handleAddNewRow(userId)}
        disabled={saving}
      >
        + 내 코드 추가
      </button>

      {/* 구분선 + 저장 */}
      <span className="toolbar-sep" />
      <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
        {saving ? '저장 중...' : '저장'}
      </button>

      {/* admin 코드 가져오기: 모든 사용자에게 표시 */}
      <button
        className="btn btn-outline"
        onClick={() => handleImportFromAdmin('SECTOR')}
        disabled={saving || loading}
      >
        섹터 가져오기
      </button>
      <button
        className="btn btn-outline"
        onClick={() => handleImportFromAdmin('TERM_TYPE')}
        disabled={saving || loading}
      >
        투자기간 가져오기
      </button>
    </>
  );

  return (
    <PageLayout title="공통 코드 관리" toolbar={toolbar} message={message}>
      <div className="grid-wrapper">
        <table className="grid-table code-table">
          <thead>
            <tr>
              <th style={{ width: 100 }}>코드타입</th>
              {isAdmin && <th style={{ width: 90 }}>소유자</th>}
              <th style={{ width: 110 }}>코드ID</th>
              <th style={{ width: 160 }}>코드명</th>
              <th className="col-center" style={{ width: 70 }}>정렬</th>
              <th className="col-center" style={{ width: 50 }}>사용</th>
              <th className="col-center" style={{ width: 60 }}>삭제</th>
              <th>비고</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colSpan} className="grid-empty">불러오는 중...</td></tr>
            ) : visibleCodes.length === 0 && newRows.length === 0 ? (
              <tr><td colSpan={colSpan} className="grid-empty">데이터가 없습니다.</td></tr>
            ) : (
              <>
                {visibleCodes.map(code => {
                  const changed = isRowChanged(code.id);
                  const rowClass = [
                    code.user_id === 'admin' ? 'cm-row-admin' : 'cm-row-own',
                    changed ? 'cm-row-changed' : '',
                  ].filter(Boolean).join(' ');

                  return (
                    <tr key={code.id} className={rowClass}>
                      <td>
                        <span className={`type-badge type-${code.code_type}`}>
                          {getTypeLabel(code.code_type)}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="cell-userid">{code.user_id}</td>
                      )}
                      <td>
                        {code.can_edit ? (
                          <input
                            className="cell-input"
                            value={getVal(code, 'code_id')}
                            onChange={e => handleCellChange(code.id, 'code_id', e.target.value)}
                            maxLength={20}
                          />
                        ) : (
                          <span className="cell-readonly">{code.code_id}</span>
                        )}
                      </td>
                      <td>
                        {code.can_edit ? (
                          <input
                            className="cell-input"
                            value={getVal(code, 'code_name')}
                            onChange={e => handleCellChange(code.id, 'code_name', e.target.value)}
                            maxLength={100}
                          />
                        ) : (
                          <span className="cell-readonly">{code.code_name}</span>
                        )}
                      </td>
                      <td className="col-center">
                        {code.can_edit ? (
                          <input
                            className="cell-input cell-number"
                            type="number"
                            value={getVal(code, 'sort_order')}
                            onChange={e => handleCellChange(code.id, 'sort_order', Number(e.target.value))}
                            min={0} max={9999}
                          />
                        ) : (
                          <span>{code.sort_order}</span>
                        )}
                      </td>
                      <td className="col-center">
                        {code.can_edit ? (
                          <input
                            type="checkbox"
                            checked={getVal(code, 'is_active')}
                            onChange={e => handleCellChange(code.id, 'is_active', e.target.checked)}
                          />
                        ) : (
                          <input type="checkbox" checked={code.is_active} readOnly disabled />
                        )}
                      </td>
                      <td className="col-center">
                        {code.can_edit ? (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(code.id, code.code_id)}
                            disabled={saving}
                          >
                            삭제
                          </button>
                        ) : (
                          <span className="tooltip-wrapper" title="admin 코드는 수정할 수 없습니다">
                            <button className="btn btn-danger btn-sm" disabled>삭제</button>
                          </span>
                        )}
                      </td>
                      <td className="col-bigo"></td>
                    </tr>
                  );
                })}

                {newRows.map(row => (
                  <tr key={row._tempId} className="cm-row-new">
                    <td>
                      {filterType ? (
                        <span className={`type-badge type-${row.code_type}`}>
                          {getTypeLabel(row.code_type)}
                        </span>
                      ) : (
                        <select
                          className="cell-input"
                          value={row.code_type}
                          onChange={e => handleNewRowChange(row._tempId, 'code_type', e.target.value)}
                        >
                          {codeTypes.map(t => (
                            <option key={t.code_type} value={t.code_type}>{t.label}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="cell-userid">{row.user_id}</td>
                    )}
                    <td>
                      <input
                        className="cell-input"
                        value={row.code_id}
                        onChange={e => handleNewRowChange(row._tempId, 'code_id', e.target.value)}
                        maxLength={20}
                        placeholder="코드ID"
                        autoFocus
                      />
                    </td>
                    <td>
                      <input
                        className="cell-input"
                        value={row.code_name}
                        onChange={e => handleNewRowChange(row._tempId, 'code_name', e.target.value)}
                        maxLength={100}
                        placeholder="코드명"
                      />
                    </td>
                    <td className="col-center">
                      <input
                        className="cell-input cell-number"
                        type="number"
                        value={row.sort_order}
                        onChange={e => handleNewRowChange(row._tempId, 'sort_order', Number(e.target.value))}
                        min={0} max={9999}
                      />
                    </td>
                    <td className="col-center">
                      <input
                        type="checkbox"
                        checked={row.is_active}
                        onChange={e => handleNewRowChange(row._tempId, 'is_active', e.target.checked)}
                      />
                    </td>
                    <td className="col-center">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleRemoveNewRow(row._tempId)}
                      >
                        취소
                      </button>
                    </td>
                    <td className="col-bigo"></td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
};

export default CodeMasterPage;
